import type { TrianglesJson } from 'contracts/PMIJson';
import type { MeshBasicMaterial, Color } from 'three';
import { BufferGeometry, Vector3, Float32BufferAttribute, Mesh } from 'three';

//eslint-disable-next-line no-restricted-imports
import { getDoubleSideMaterial } from '../../materials';

const maxIndices = 65535; //(size of Uint16Array less one)

export const parseTriangles = (
  pointsJson: TrianglesJson['triangles'],
  color: Color | null,
): Mesh<BufferGeometry, MeshBasicMaterial>[] => {
  const positionsChunks: number[][] = [[]];
  const normalsChunks: number[][] = [[]];

  let [positions] = positionsChunks;
  let [normals] = positionsChunks;

  //facePoints is for collecting the 3 vertices of each triangle face in order to compute normal
  const facePointA: Vector3 = new Vector3();
  const facePointB: Vector3 = new Vector3();
  const facePointC: Vector3 = new Vector3();

  //cb and ab are for computing vectors between vertices in facePoints in order to compute normal
  const cb = new Vector3();
  const ab = new Vector3();

  //Collect normals
  let i = 0;
  const lastTriangleStartIndex = pointsJson.length - 8;
  while (i <= lastTriangleStartIndex) {
    if (positions.length + 9 > maxIndices) {
      positionsChunks.push((positions = []));
      normalsChunks.push((normals = []));
    }
    const ax = pointsJson[i];
    const ay = pointsJson[i + 1];
    const az = pointsJson[i + 2];
    const bx = pointsJson[i + 3];
    const by = pointsJson[i + 4];
    const bz = pointsJson[i + 5];
    const cx = pointsJson[i + 6];
    const cy = pointsJson[i + 7];
    const cz = pointsJson[i + 8];
    facePointA.set(ax, ay, az);
    facePointB.set(bx, by, bz);
    facePointC.set(cx, cy, cz);
    //Compute normals for each face's vertex (because normals were not provided)
    cb.subVectors(facePointC, facePointB);
    ab.subVectors(facePointA, facePointB);
    cb.cross(ab).normalize();
    const [nx, ny, nz] = [cb.x, cb.y, cb.z];
    positions.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    normals.push(nx, ny, nz, nx, ny, nz, nx, ny, nz); //normal is repeated for each vertex
    i += 9;
  }

  return positionsChunks.map((positions, i) => {
    const geometry = new BufferGeometry();
    geometry
      .setAttribute('position', new Float32BufferAttribute(positions, 3))
      .setAttribute('normal', new Float32BufferAttribute(normalsChunks[i], 3))
      .computeBoundingBox();
    return new Mesh<BufferGeometry, MeshBasicMaterial>(
      geometry,
      getDoubleSideMaterial(color),
    );
  });
};
